import type { RecurlyError } from '@recurly/recurly-js';
import { loadStripe } from '@stripe/stripe-js';
import type { StripeExpressCheckoutElementOptions, StripePaymentElement } from '@stripe/stripe-js';
import type { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js/dist/stripe-js/elements/express-checkout';
import type { StripeElementLocale, StripeElements, StripeElementsOptionsMode } from '@stripe/stripe-js/dist/stripe-js/elements-group';
import classNames from 'classnames';
import type { userAgent } from 'next/server';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type PaymentMethod from '@alltrails/amplitude/enums/PaymentMethod';
import type UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import logPurchaseButtonTapped from '@alltrails/amplitude/events/logPurchaseButtonTapped';
import logPurchaseFailed from '@alltrails/amplitude/events/logPurchaseFailed';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import Typography from '@alltrails/core/components/Typography';
import { useClientNavigationSearchParams } from '@alltrails/core/hooks/useClientNavigation';
import useIsVirtualKeyboardOpen from '@alltrails/core/hooks/useIsVirtualKeyboardOpen';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import type SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import { ApiError } from '@alltrails/core/utils/api';
import { getCopyrightText } from '@alltrails/core/utils/getCopyrightText';
import logError from '@alltrails/core/utils/logError';
import { executeRecaptcha } from '@alltrails/core/utils/recaptcha-helpers';
import Button from '@alltrails/denali/components/Button';
import Divider from '@alltrails/denali/components/Divider';
import Link from '@alltrails/denali/components/Link';
import TextInput from '@alltrails/denali/components/TextInput';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import {
  COLOR_TEXT_ERROR,
  COLOR_TEXT_TERTIARY,
  FONT_FAMILY_PRIMARY,
  FONT_LETTERSPACING_200,
  FONT_LINEHEIGHT_DEFAULT,
  FONT_SIZE_100,
  FONT_SIZE_200,
  FONT_WEIGHT_NORMAL
} from '@alltrails/denali/tokens';
import type CountryCode from '@alltrails/types/CountryCode';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { Plan } from '@alltrails/types/Plan';
import type PaymentTokenPayload from '../../types/PaymentTokenPayload';
import type ProResponse from '../../types/ProResponse';
import { CARD_VALIDATION_NAMES } from '../../types/RecurlyTokenization';
import getApplePayConfigForStripe from '../../utils/getApplePayConfigForStripe';
import getAvailablePlans from '../../utils/getAvailablePlans';
import getPurchaseScreenTypePlan from '../../utils/getPurchaseScreenTypePlan';
import { getPriceTotal, isTrial } from '../../utils/plan';
import toMinorUnits from '../../utils/toMinorUnits';
import BillingCountrySelector from '../BillingCountrySelector';
import ComplianceCheckbox from '../ComplianceCheckbox/ComplianceCheckbox';
import useTokenizedFormValidation from '../TokenizedCreditCardForm/hooks/useTokenizedFormValidation';
import tokenizedStyles from '../TokenizedCreditCardForm/TokenizedCreditCardForm.module.scss';
import { hasValidLastName, isValidPostalCode } from '../TokenizedCreditCardForm/utils';
import styles from './Checkout.module.scss';
import CouponCode from './CouponCode';
import ErrorText from './ErrorText';
import LoadingOverlay from './LoadingOverlay';
import { PaymentOptionValues } from './PaymentOptionValues';
import { payPalSupportedCurrencies } from './payPalSupportedCurrencies';
import StripeApplePayButton from './StripeApplePayButton';
import StripeCheckoutModalFooter from './StripeCheckoutModalFooter';
import StripePayPalButton from './StripePayPalButton';
import TogglePaymentButton from './TogglePaymentButton/TogglePaymentButton';
import TrialReminder from './TrialReminder';

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY, {
  stripeAccount: process.env.NEXT_PUBLIC_STRIPE_ACCOUNT
});

const STRIPE_PAYMENT_ELEMENT_TYPE_TO_PAYMENT_METHOD_TYPE: Partial<Record<string, PaymentTokenPayload['payment_method_type']>> = {
  apple_pay: 'apple_pay',
  card: 'card',
  google_pay: 'google_pay',
  link: 'link',
  [process.env.NEXT_PUBLIC_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD]: 'paypal'
};

function getPaymentMethodTypeForToken(selectedPaymentType: string): PaymentTokenPayload['payment_method_type'] {
  const mapped = STRIPE_PAYMENT_ELEMENT_TYPE_TO_PAYMENT_METHOD_TYPE[selectedPaymentType];
  if (mapped) {
    return mapped;
  }
  logError(new Error(`Unknown Stripe payment method: ${selectedPaymentType}`));
  return selectedPaymentType as PaymentTokenPayload['payment_method_type'];
}

function isRecurlyError(error: unknown): error is RecurlyError {
  return typeof error === 'object' && error !== null && 'message' in error && 'code' in error;
}

function getAnalyticsPaymentMethod(selectedPaymentType: string): PaymentMethod {
  const mapped = STRIPE_PAYMENT_ELEMENT_TYPE_TO_PAYMENT_METHOD_TYPE[selectedPaymentType];
  if (mapped) {
    return mapped === 'card' ? 'credit_card' : mapped;
  }
  logError(new Error(`Unknown Stripe payment method: ${selectedPaymentType}`));
  return selectedPaymentType as PaymentMethod;
}

// https://github.com/stripe/stripe-js/blob/master/types/stripe-js/elements/express-checkout.d.ts#L192
function getPaymentMethodTypeFromExpressCheckoutEvent(
  expressPaymentType: StripeExpressCheckoutElementConfirmEvent['expressPaymentType']
): PaymentTokenPayload['payment_method_type'] {
  switch (expressPaymentType) {
    case 'apple_pay':
      return 'apple_pay';
    case 'google_pay':
      return 'google_pay';
    case 'link':
      return 'link';
    default:
      logError(new Error(`Unknown Stripe express checkout payment type: ${expressPaymentType}`));
      return expressPaymentType as PaymentTokenPayload['payment_method_type'];
  }
}

const LANGUAGE_REGION_TO_STRIPE_LOCALE: Record<LanguageRegionCode, StripeElementLocale> = {
  'da-DK': 'da',
  'de-DE': 'de',
  'en-GB': 'en-GB',
  'en-US': 'en',
  'es-ES': 'es-ES',
  'es-MX': 'es-419',
  'fr-FR': 'fr-FR',
  'it-IT': 'it-IT',
  'nb-NO': 'nb',
  'nl-NL': 'nl',
  'pl-PL': 'pl',
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt',
  'sv-SE': 'sv'
};

type Props = {
  ariaLabelledBy?: string;
  // When provided, replaces the default "Billed today"/subheading rows with custom content
  customBillingInfo?: ReactNode;
  billedHeading?: ReactNode;
  billedSubheading?: ReactNode;
  billedSubheadingClassName?: string;
  billedSubheadingRight?: ReactNode;
  billedToday: ReactNode;
  billingCountry: CountryCode;
  buttonText: ReactNode;
  canUpgradeExistingSubscription: boolean;
  className?: string;
  coupon?: Coupon;
  couponError?: string;
  currencyCode: CurrencyCode;
  finePrint?: ReactNode;
  heading: ReactNode;
  headingClassName?: string;
  isReiCheckout?: boolean;
  isComplianceCheckboxEnabled?: boolean;
  isLinkExpressCheckoutEnabled: boolean;
  isLinkPaymentElementEnabled: boolean;
  isMultiyearConversion?: boolean;
  isRadioUiEnabled?: boolean;
  isUpgrade?: boolean;
  onBillingCountryChange: (countryCode: CountryCode, response: unknown) => void;
  onSubmitGiftCardRedemption: (payload: { recaptcha: string }) => void;
  onPaymentToken: (payload: PaymentTokenPayload) => void | Promise<void>;
  plan: Plan;
  promoText?: ReactNode;
  purchaseTier: PurchaseTier;
  setCoupon?: (coupon: Coupon) => void;
  setPlanToAnnual?: () => void;
  subheading?: ReactNode;
  subscription?: SubscriptionsNewResponse['subscription'];
  webProPurchaseLocation: WebProPurchaseLocation;
  whenExpired?: string;
} & ({ isCheckoutModal: true; features?: never; trigger: UpsellTrigger } | { isCheckoutModal?: never; features: ReactNode; trigger?: never });

function StripeCheckoutBase({
  ariaLabelledBy,
  customBillingInfo,
  billedHeading,
  billedSubheading,
  billedSubheadingClassName,
  billedSubheadingRight,
  billedToday,
  billingCountry,
  buttonText,
  canUpgradeExistingSubscription,
  className,
  coupon,
  couponError,
  currencyCode,
  features,
  finePrint,
  heading,
  headingClassName,
  isReiCheckout,
  isCheckoutModal,
  isComplianceCheckboxEnabled = false,
  isLinkExpressCheckoutEnabled,
  isLinkPaymentElementEnabled,
  isMultiyearConversion,
  isRadioUiEnabled,
  isUpgrade,
  onBillingCountryChange,
  onSubmitGiftCardRedemption,
  onPaymentToken,
  plan,
  promoText,
  purchaseTier,
  setCoupon,
  setPlanToAnnual,
  subheading,
  subscription,
  trigger,
  webProPurchaseLocation,
  whenExpired
}: Props) {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const publicBaseUrl = usePublicBaseUrl();
  // `applePayConfig` is a dependency of the Stripe `useEffect` below; keep a stable object reference so we
  // do not tear down and recreate Elements on every unrelated re-render.
  const applePayConfig = useMemo(
    () =>
      webProPurchaseLocation !== 'gift page'
        ? {
            recurringPaymentRequest: getApplePayConfigForStripe(
              {
                canUpgradeExistingSubscription,
                coupon,
                isMultiyearConversion,
                plan,
                publicBaseUrl,
                purchaseTier,
                subscription,
                whenExpired
              },
              intl
            )
          }
        : undefined,
    [
      canUpgradeExistingSubscription,
      coupon,
      intl,
      isMultiyearConversion,
      plan,
      publicBaseUrl,
      purchaseTier,
      subscription,
      webProPurchaseLocation,
      whenExpired
    ]
  );
  const isMobile = useIsMobileSizedScreen();
  const { isKeyboardOpen } = useIsVirtualKeyboardOpen();
  const [focusedPaymentFields, setFocusedPaymentFields] = useState({ stripe: false, name: false, postal: false });
  const setPaymentFieldFocus = (field: 'stripe' | 'name' | 'postal', focused: boolean) => {
    setFocusedPaymentFields(prev => ({ ...prev, [field]: focused }));
  };
  const isPaymentFieldFocused = Object.values(focusedPaymentFields).some(Boolean);
  const searchParams = useClientNavigationSearchParams();

  // https://www.figma.com/design/dOj6Te4I0TVguYevn6Fj5g/Web-Checkout-Improvements?node-id=1417-8321&t=aSZIfvtiFLXWHrXx-0
  const isIosNativeApp = isMobile && searchParams.get('app_source') === 'iOS';
  const isAndroidNativeApp = searchParams.get('app_source') === 'android';
  const isNativeApp = isIosNativeApp || isAndroidNativeApp;
  const hideGooglePay = searchParams.get('hideGooglePay') === 'true';
  const browserName = useSelector((state: { browser?: ReturnType<typeof userAgent>['browser'] }) => state.browser?.name);
  const isChrome = browserName?.includes('Chrome');
  // Apple/Google Pay have UX issues for Plus-to-Peak upgrades and multiyear conversions on Stripe Elements,
  // so hide those wallets for these flows (GROW-17814). isIosNativeApp/isAndroidNativeApp are ignored here
  // because upgrades/multiyear do not occur in native apps.
  const hideWalletPayments = canUpgradeExistingSubscription || !!isMultiyearConversion;
  // The express checkout element only offers Apple Pay, Google Pay, and Link. When the wallets are hidden and
  // Link express checkout is disabled, there is nothing to render, so skip the element and its divider.
  const showExpressCheckout = !!isChrome && (!hideWalletPayments || isLinkExpressCheckoutEnabled);

  const [paymentMethodOrder, setPaymentMethodOrder] = useState<string[] | undefined>(() =>
    isIosNativeApp || (isMobile && browserName?.includes('Safari')) ? ['apple_pay'] : undefined
  );

  const { setClientValidationErrors, validationErrors } = useTokenizedFormValidation();
  const { nameError, postalCodeError } = validationErrors;

  const [submitting, setSubmitting] = useState(false);
  const [expressCheckoutSubmitting, setExpressCheckoutSubmitting] = useState(false);
  const [errors, setErrors] = useState<ProResponse['errors']>();
  const [fullName, setFullName] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [isComplianceChecked, setIsComplianceChecked] = useState(false);
  const [complianceError, setComplianceError] = useState(false);

  // Gates payment submission on the California compliance checkbox when it is shown.
  const validateCompliance = (paymentMethod: PaymentMethod): boolean => {
    if (complianceError) {
      setComplianceError(false);
    }

    const hasComplianceError = isComplianceCheckboxEnabled && !isComplianceChecked;

    if (hasComplianceError) {
      setComplianceError(true);
      logPurchaseFailed({
        error_code: 'terms and conditions unchecked',
        payment_method: paymentMethod
      });
    }

    return !hasComplianceError;
  };

  // Keep a ref to the latest validateCompliance so it can be invoked from the Stripe
  // Express Checkout `click` callback, which is registered once inside an effect and
  // would otherwise close over stale state.
  const validateComplianceRef = useRef(validateCompliance);
  validateComplianceRef.current = validateCompliance;

  const expressMountRef = useRef<HTMLDivElement>(null);
  const paymentMountRef = useRef<HTMLDivElement>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const expressCheckoutElementRef = useRef<{ unmount: () => void } | null>(null);
  const paymentElementRef = useRef<StripePaymentElement | null>(null);
  const stripeRef = useRef<Awaited<ReturnType<typeof loadStripe>>>(null);

  const [selectedPaymentType, setSelectedPaymentType] = useState<string>(isIosNativeApp ? 'apple_pay' : 'card');

  // In the REI checkout flow on mobile, the sticky footer + the virtual keyboard takes up a lot of the viewport height, so we
  // "un-stick" the footer when a card field has the keyboard open
  const isFooterSticky = !(isReiCheckout && isMobile && selectedPaymentType === 'card' && (isKeyboardOpen || isPaymentFieldFocused));

  const showBillingCountry = billingCountry && !isUpgrade && !isReiCheckout;
  const showPostalCode = billingCountry === 'CA' || billingCountry === 'US';
  const isGiftCard = coupon?.type === 'GiftCard';

  const elementsModePaymentOrSetup = useMemo(() => {
    const amountMinorUnits = toMinorUnits(getPriceTotal(plan, coupon), plan.currency);
    if (isTrial(plan, coupon) || canUpgradeExistingSubscription || amountMinorUnits === 0) {
      return { mode: 'setup' as const };
    }
    return { amount: amountMinorUnits, mode: 'payment' as const };
  }, [canUpgradeExistingSubscription, coupon, plan]);

  function logExpressCheckoutPurchaseButtonTapped(event) {
    logPurchaseButtonTapped({
      payment_method: getAnalyticsPaymentMethod(event.expressPaymentType),
      product_type: purchaseTier === 'peak' ? 'peak' : 'plus',
      purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
      web_purchase_location: webProPurchaseLocation,
      ...(trigger && { upsell_trigger: trigger })
    });
  }

  useEffect(() => {
    async function setupStripe() {
      let stripe: Awaited<ReturnType<typeof loadStripe>>;
      try {
        stripe = await stripePromise;
      } catch (e) {
        logError(new Error('loadStripe failed', { cause: e }));
        logPurchaseFailed({ error_code: 'stripe_not_loaded', failure_type: 'loadStripe promise rejected' });
        return;
      }

      stripeRef.current = stripe;

      // loadStripe() can return null if the script fails to load or the key is invalid
      if (!stripe) {
        logPurchaseFailed({ error_code: 'stripe_not_loaded' });
        return;
      }

      // https://github.com/stripe/stripe-js/blob/master/types/stripe-js/elements-group.d.ts#L754
      const options: StripeElementsOptionsMode = {
        currency: currencyCode.toLowerCase(),
        locale: LANGUAGE_REGION_TO_STRIPE_LOCALE[languageRegionCode],
        ...(payPalSupportedCurrencies.includes(currencyCode) &&
          !isIosNativeApp &&
          !isAndroidNativeApp && {
            customPaymentMethods: [{ id: process.env.NEXT_PUBLIC_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD, options: { type: 'static' } }]
          }),
        // available payment method types are pushed down from Stripe // paymentMethodTypes: ['card'],
        fonts: [
          {
            family: FONT_FAMILY_PRIMARY,
            src: 'url(https://cdn-assets-2.alltrails.com/assets/aeonik-regular-ee9d02127cfdb2aef717576a3881369ecc5e5f5c940bd2cc61a077c848c40b69.woff2)',
            weight: '400'
          },
          {
            family: FONT_FAMILY_PRIMARY,
            src: 'url(https://cdn-assets-2.alltrails.com/assets/aeonik-medium-81496b49e1e345e2a7803ddfe8e98ddd695b66f385f43b66975fa53d8215abeb.woff2)',
            weight: '500'
          },
          {
            family: FONT_FAMILY_PRIMARY,
            src: 'url(https://cdn-assets-2.alltrails.com/assets/aeonik-semibold-cc80f9e0c793ecfd7f632df9ce82f888e5f4a26cde3dee08c930492f15d6475f.woff2)',
            weight: '600'
          }
        ],
        // https://docs.stripe.com/elements/appearance-api
        appearance: {
          inputs: 'condensed',
          rules: {
            '.Input--invalid': {
              borderColor: COLOR_TEXT_ERROR
            },
            '.Label': {
              color: COLOR_TEXT_TERTIARY,
              // fontFeatureSettings: '"ss02"',
              fontSize: `${FONT_SIZE_200 / 16}rem`,
              letterSpacing: `${FONT_LETTERSPACING_200 / 16}rem`,
              lineHeight: String(FONT_LINEHEIGHT_DEFAULT)
            },
            '.Error': {
              fontSize: `${FONT_SIZE_100 / 16}rem`,
              fontWeight: String(FONT_WEIGHT_NORMAL)
            }
          },
          variables: {
            colorDanger: COLOR_TEXT_ERROR,
            fontFamily: `${FONT_FAMILY_PRIMARY}, sans-serif`
          }
        },
        setupFutureUsage: 'off_session',
        excludedPaymentMethodTypes: ['cashapp'],
        ...elementsModePaymentOrSetup
        // paymentMethodConfiguration uses the default search for "pmc_" in devtools network to find the session,
      } as const;

      let elements;
      try {
        elements = stripe.elements(options);
      } catch (e) {
        setErrors({ base: [] });
        logError(new Error('create elements returned an error', { cause: e }));
        logPurchaseFailed({
          error_code: (e as Error).message,
          failure_type: 'create elements returned an error'
        });
      }
      elementsRef.current = elements;

      if (showExpressCheckout) {
        // https://docs.stripe.com/js/element/express_checkout_element
        const expressCheckoutOptions: StripeExpressCheckoutElementOptions = {
          ...(applePayConfig && { applePay: applePayConfig }),
          billingAddressRequired: true,
          layout: {
            overflow: 'never'
          },
          paymentMethods: {
            applePay: hideWalletPayments ? 'never' : 'always', // Always show Apple Pay so it appears on Chrome and uses the QR code,
            link: isLinkExpressCheckoutEnabled ? 'auto' : 'never',
            googlePay: hideWalletPayments || hideGooglePay ? 'never' : 'always' // Stripe will hide Google Pay even when it is available and Stripe doesn't provide logging
          }
        };

        let expressCheckoutElement;
        try {
          expressCheckoutElement = elements.create('expressCheckout', expressCheckoutOptions);
        } catch (e) {
          logError(new Error('create express checkout element returned an error', { cause: e }));
          logPurchaseFailed({
            error_code: 'create express checkout element returned an error',
            failure_type: 'create express checkout element returned an error'
          });
          return;
        }

        // https://docs.stripe.com/js/elements_object/express_checkout_element_click_event
        expressCheckoutElement.on('click', event => {
          logExpressCheckoutPurchaseButtonTapped(event);
          // Validate the compliance checkbox before opening the wallet sheet; not calling
          // event.resolve() aborts the express checkout flow.
          const isComplianceValidated = validateComplianceRef.current(getAnalyticsPaymentMethod(event.expressPaymentType));
          if (!isComplianceValidated) {
            return;
          }
          event.resolve();
        });

        // https://docs.stripe.com/js/elements_object/express_checkout_element_confirm_event
        expressCheckoutElement.on('confirm', async event => {
          setExpressCheckoutSubmitting(true);
          try {
            const recaptcha = await executeRecaptcha('userPaymentAction');

            let confirmationTokenResponse;
            try {
              // https://docs.stripe.com/js/confirmation_tokens/create_confirmation_token
              confirmationTokenResponse = await stripe.createConfirmationToken({
                elements: elementsRef.current!,
                params: {
                  payment_method_data: {
                    billing_details: {
                      // billing details come from the Google Pay or Apple Pay wallet automatically and we want to send them back to Stripe
                      name: event.billingDetails?.name,
                      email: event.billingDetails?.email,
                      phone: event.billingDetails?.phone,
                      address: event.billingDetails?.address
                    }
                  },
                  return_url: 'https://www.alltrails.com'
                }
              });
            } catch (e) {
              // this is a developer error so we don't want to show it to the user
              setErrors({ base: [] });

              logError(new Error('express checkout create confirmation threw an error', { cause: e }));

              logPurchaseFailed({
                error_code: (e as Error).message,
                failure_type: 'express checkout create confirmation threw an error'
              });

              setExpressCheckoutSubmitting(false);
              return;
            }

            if (confirmationTokenResponse.error) {
              setErrors({ base: [] });
              logError(new Error('express checkout create confirmation token returned an error', { cause: confirmationTokenResponse.error }));
              setExpressCheckoutSubmitting(false);
              return;
            }

            if (!confirmationTokenResponse.confirmationToken) {
              setErrors({ base: [] });
              logError(new Error('express checkout no confirmation token returned'));
              setExpressCheckoutSubmitting(false);
              return;
            }

            try {
              await onPaymentToken({
                payment_method_type: getPaymentMethodTypeFromExpressCheckoutEvent(event.expressPaymentType),
                recaptcha,
                token: confirmationTokenResponse.confirmationToken.id,
                tokenization_source: 'stripe'
              });
            } catch (e) {
              logError(new Error('express checkout onPaymentToken failed', { cause: e }));
              logPurchaseFailed({
                ...(e instanceof ApiError ? { error_code: e.data.base[0] } : isRecurlyError(e) ? { error_code: e.code ? e.code : e.message } : {}),
                payment_method: getAnalyticsPaymentMethod(event.expressPaymentType),
                failure_type: 'express checkout onPaymentToken failed'
              });

              if (e instanceof ApiError) {
                setErrors(e.data);
              } else if (isRecurlyError(e)) {
                setErrors({ base: [e.message] });
              } else {
                setErrors({ base: [] });
              }
              setExpressCheckoutSubmitting(false);
              return;
            }
          } catch (e) {
            logError(new Error('express checkout confirm failed', { cause: e }));
            logPurchaseFailed({
              error_code: e instanceof Error ? e.message : 'unknown_error',
              payment_method: getAnalyticsPaymentMethod(event.expressPaymentType),
              failure_type: 'express checkout confirm failed'
            });
            setErrors({ base: [] });
            setExpressCheckoutSubmitting(false);
          }
        });

        expressCheckoutElement.on('loaderror', event => {
          console.error('Express Checkout failed to load:', event.error);
        });

        expressCheckoutElementRef.current = expressCheckoutElement;
        if (expressMountRef.current) {
          expressCheckoutElement.mount(expressMountRef.current);
        }
      }

      // https://docs.stripe.com/js/elements_object/create_payment_element
      const paymentElement = elements.create('payment', {
        ...(applePayConfig && { applePay: applePayConfig }),
        fields: {
          billingDetails: {
            address: 'if_required'
          }
        },
        layout: {
          type: 'accordion',
          defaultCollapsed: false,
          ...(!isChrome && { radios: 'always' })
        },
        ...(paymentMethodOrder && { paymentMethodOrder }),
        terms: {
          applePay: 'never',
          auBecsDebit: 'never',
          bancontact: 'never',
          card: 'never',
          cashapp: 'never',
          googlePay: 'never',
          ideal: 'never',
          paypal: 'never',
          sepaDebit: 'never',
          sofort: 'never',
          usBankAccount: 'never'
        },
        wallets: {
          ...(hideWalletPayments && { applePay: 'never', googlePay: 'never' }),
          ...(hideGooglePay && { googlePay: 'never' }),
          link: isLinkPaymentElementEnabled ? 'auto' : 'never'
        }
      });

      paymentElement.on('change', event => {
        setSelectedPaymentType(event.value.type);
      });

      paymentElement.on('focus', () => setPaymentFieldFocus('stripe', true));
      paymentElement.on('blur', () => setPaymentFieldFocus('stripe', false));

      paymentElementRef.current = paymentElement;
      if (paymentMountRef.current) {
        paymentElement.mount(paymentMountRef.current);
      }
    }

    setupStripe();

    return () => {
      expressCheckoutElementRef.current?.unmount();
      expressCheckoutElementRef.current = null;
      paymentElementRef.current?.unmount();
      paymentElementRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- plan, coupon, currencyCode and canUpgradeExistingSubscription are intentionally omitted; `elements.update` runs in the effect below when currency, mode, or amount should change (canUpgradeExistingSubscription also affects applePayConfig when applicable)
  }, [
    applePayConfig,
    browserName,
    isChrome,
    isAndroidNativeApp,
    isIosNativeApp,
    isMobile,
    languageRegionCode,
    onPaymentToken,
    purchaseTier,
    trigger,
    webProPurchaseLocation,
    paymentMethodOrder, // since update doesn't support paymentMethodOrder when need to remount the payment element for iOS native app
    isLinkExpressCheckoutEnabled,
    isLinkPaymentElementEnabled,
    hideWalletPayments,
    hideGooglePay,
    showExpressCheckout
  ]);

  useEffect(() => {
    if (elementsRef.current) {
      try {
        //https://docs.stripe.com/js/elements_object/update
        elementsRef.current.update({
          currency: currencyCode.toLowerCase(),
          ...elementsModePaymentOrSetup
        });
      } catch (e) {
        logError(new Error('update elements returned an error', { cause: e }));
        logPurchaseFailed({
          error_code: (e as Error).message,
          failure_type: 'update elements returned an error'
        });
      }
    }
  }, [currencyCode, elementsModePaymentOrSetup]);

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();

    if (submitting) {
      return;
    }

    setSubmitting(true);

    try {
      setErrors(undefined);

      if (!elementsRef.current) {
        logPurchaseFailed({ error_code: 'stripe elements not loaded', failure_type: 'stripe elements not loaded' });
        logError('stripe elements not loaded');
        return;
      }

      logPurchaseButtonTapped({
        payment_method: getAnalyticsPaymentMethod(selectedPaymentType),
        product_type: purchaseTier === 'peak' ? 'peak' : 'plus',
        purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
        web_purchase_location: webProPurchaseLocation,
        ...(trigger && { upsell_trigger: trigger })
      });

      // Validate compliance checkbox; skip the gate for gift-card redemptions (no auto-renewal), matching CheckoutBase.
      const isComplianceValidated = validateCompliance(getAnalyticsPaymentMethod(selectedPaymentType));
      if (!isComplianceValidated && !isGiftCard) {
        return;
      }

      // Note: selectedPaymentMethod is never returned if there is an error or when link is selected so we have to use selectedPaymentType
      const submitResult = await elementsRef.current.submit();

      if (!isGiftCard) {
        if (selectedPaymentType === 'card') {
          const hasFieldErrors = fullName.length < 1 || !hasValidLastName(fullName) || (showPostalCode && !isValidPostalCode(postalCode));

          // only the first error is returned in Stripe's submitResult
          if (hasFieldErrors || submitResult.error) {
            setClientValidationErrors(CARD_VALIDATION_NAMES.NAME, fullName);
            if (showPostalCode) {
              setClientValidationErrors(CARD_VALIDATION_NAMES.POSTAL_CODE, postalCode);
            }
            return;
          }
        }

        // validation errors are automatically displayed under the credit card fields
        if (submitResult.error && submitResult.error.message) {
          logPurchaseFailed({ error_code: submitResult.error.message, failure_type: 'submit returned an error we displayed to the user' });
          setErrors({ base: [submitResult.error.message] });
          return;
        }
      }

      const stripe = stripeRef.current;

      if (!stripe) {
        logPurchaseFailed({ error_code: 'stripe not loaded', failure_type: 'stripe not loaded' });
        return;
      }

      if (submitResult.error) {
        logPurchaseFailed({ error_code: submitResult.error.message, failure_type: 'submit returned an error without a message' });
        return;
      }

      if (selectedPaymentType === process.env.NEXT_PUBLIC_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD) {
        recurly.configure(process.env.NEXT_PUBLIC_RECURLY_KEY);

        const payPal = recurly.PayPal({
          display: {
            displayName: purchaseTier === 'peak' ? 'AllTrails Peak' : 'AllTrails Plus',
            amount: String(getPriceTotal(plan, coupon))
          }
        });

        payPal.on('error', (error: RecurlyError) => {
          logError(error);
          logPurchaseFailed({ error_code: error.message, payment_method: 'paypal' });
        });

        payPal.on('token', async token => {
          try {
            setSubmitting(true);

            const recaptcha = await executeRecaptcha('userPaymentAction');

            await onPaymentToken({
              payment_method_type: 'paypal',
              recaptcha,
              token: token.id,
              tokenization_source: 'recurly_js'
            });
          } catch (e) {
            logPurchaseFailed({
              ...(e instanceof ApiError ? { error_code: e.data.base[0] } : {}),
              payment_method: 'paypal'
            });

            if (e instanceof ApiError) {
              setErrors(e.data);
            } else {
              setErrors({ base: [] });
            }
            setSubmitting(false);
          }
        });

        payPal.start();
        return;
      }

      const recaptcha = await executeRecaptcha('userPaymentAction');

      if (isGiftCard) {
        await onSubmitGiftCardRedemption({ recaptcha });
        return;
      }

      let confirmationTokenResponse;
      try {
        // https://docs.stripe.com/js/confirmation_tokens/create_confirmation_token
        confirmationTokenResponse = await stripe.createConfirmationToken({
          elements: elementsRef.current,
          params: {
            // only pass billing details if the payment method is card we don't want to override apple pay billing details
            ...(selectedPaymentType === 'card' && {
              payment_method_data: {
                billing_details: {
                  address: {
                    city: null,
                    country: billingCountry,
                    line1: null,
                    state: null,
                    postal_code: postalCode ? postalCode : null
                  }
                }
              }
            }),
            return_url: 'https://www.alltrails.com'
          }
        });
      } catch (e) {
        // this is a developer error so we don't want to show it to the user
        setErrors({ base: [] });

        logError(new Error('create confirmation token returned an error', { cause: e }));

        logPurchaseFailed({
          error_code: (e as Error).message,
          failure_type: 'create confirmation token returned an error'
        });

        return;
      }

      if (confirmationTokenResponse.error) {
        setErrors({ base: [confirmationTokenResponse.error.message] });

        logPurchaseFailed({
          error_code: confirmationTokenResponse.error.message,
          failure_type: 'create confirmation token response returned an error'
        });

        return;
      }

      if (!confirmationTokenResponse.confirmationToken) {
        logPurchaseFailed({
          failure_type: 'create confirmation token returned no confirmation token'
        });
        return;
      }

      const payment_method_type = getPaymentMethodTypeForToken(selectedPaymentType);

      await onPaymentToken({
        payment_method_type,
        recaptcha,
        token: confirmationTokenResponse.confirmationToken.id,
        tokenization_source: 'stripe'
      });
    } catch (e) {
      if (e instanceof ApiError) {
        setErrors(e.data);
      } else if (isRecurlyError(e)) {
        setErrors({ base: [e.message] });
      } else {
        setErrors({ base: [] });
      }
      logError(new Error('onPaymentToken returned an error', { cause: e }));
      logPurchaseFailed({
        ...(e instanceof ApiError ? { error_code: e.data.base[0] } : isRecurlyError(e) ? { error_code: e.code ? e.code : e.message } : {}),
        failure_type: 'onPaymentToken returned an error'
      });
    } finally {
      setSubmitting(false);
    }
  }

  const isFinePrintLeftAligned = billingCountry === 'US' && !isCheckoutModal && !isMobile && !isComplianceCheckboxEnabled && !isReiCheckout;

  return (
    <form
      className={classNames(styles.content, className, {
        [styles.complianceCheckboxEnabled]: isComplianceCheckboxEnabled,
        [styles.iosWebCheckoutOptimizationEnabled]: isNativeApp,
        [styles.buttonToggleTreatmentVariant]: isNativeApp,
        [styles.hierarchyCardCtaAndTimelineVariant]: isNativeApp,
        [styles.reiCheckout]: isReiCheckout
      })}
      onSubmit={handleSubmit}
    >
      <div className={classNames(styles.columnContainer, { [styles.reiColumnContainer]: isReiCheckout })}>
        <div className={styles.tryArea}>
          <h1
            className={classNames(styles.heading, headingClassName, { [styles.complianceHeading]: isComplianceCheckboxEnabled })}
            id={ariaLabelledBy}
          >
            {heading}
          </h1>
          {isIosNativeApp && plan.isTrial && (
            <div className={styles.timeline}>
              <TrialReminder currencyCode={currencyCode} cost={plan.priceTotal} tier={purchaseTier} selected={true} />
            </div>
          )}
          {subheading && (
            <div className={styles.subheading}>
              <Typography variant="text100">{subheading}</Typography>
            </div>
          )}
          {features}
        </div>
        <div className={styles.billedCopyright}>
          <div className={styles.billedArea}>
            {customBillingInfo || (
              <div className={styles.headings}>
                <div className={styles.billed}>
                  <Typography component="div" variant="text200bold">
                    {billedHeading || <FormattedMessage defaultMessage="Billed today" />}
                  </Typography>
                  <Typography component="div" variant="text200">
                    {billedToday}
                  </Typography>
                </div>
                <div className={styles.hr} />
                {billedSubheading && (
                  <div className={classNames(styles.after, billedSubheadingClassName)}>
                    <Typography component="div" variant="text200bold">
                      {billedSubheading}
                    </Typography>
                    <Typography component="div" variant="text200">
                      {billedSubheadingRight}
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {setCoupon && setPlanToAnnual && !isIosNativeApp && (
              <div>
                <CouponCode
                  couponError={couponError}
                  coupon={coupon}
                  purchaseTier={purchaseTier}
                  setCoupon={setCoupon}
                  setPlanToAnnual={setPlanToAnnual}
                />
              </div>
            )}
          </div>
          {finePrint && isFinePrintLeftAligned && <div className={classNames(styles.fine, styles.leftAlign)}>{finePrint}</div>}
          {isComplianceCheckboxEnabled && (
            <ComplianceCheckbox
              finePrint={finePrint}
              checked={isComplianceChecked}
              onChange={checked => {
                setIsComplianceChecked(checked);
                setComplianceError(false);
              }}
              error={complianceError}
            />
          )}
          <div
            className={classNames(styles.copyright, {
              [styles.leftAlign]: isFinePrintLeftAligned,
              [styles.complianceVariant]: isComplianceCheckboxEnabled
            })}
          >
            {getCopyrightText({ intl })}
            <br />
            <div>
              <Link className={styles.link} href="/privacy" target="_blank" noUnderline>
                <FormattedMessage defaultMessage="Privacy Policy" />
              </Link>{' '}
              |{' '}
              <Link className={styles.link} href="/terms" target="_blank" noUnderline>
                <FormattedMessage defaultMessage="Terms of Service" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className={classNames(styles.columnContainer, { [styles.reiColumnContainer]: isReiCheckout })}>
        <div className={classNames(styles.form, { [styles.complianceEnabled]: isComplianceCheckboxEnabled })}>
          {showExpressCheckout && <div ref={expressMountRef} />}

          {showExpressCheckout && (
            <Divider>
              <FormattedMessage defaultMessage="Or enter payment details" />
            </Divider>
          )}

          <div className={classNames(tokenizedStyles.formInputContainer, { [styles.complianceVariant]: isComplianceCheckboxEnabled })}>
            <div className={classNames({ [styles.hidden]: isIosNativeApp && selectedPaymentType === 'apple_pay' })} ref={paymentMountRef} />
            {selectedPaymentType === 'card' && (
              <>
                <div
                  className={tokenizedStyles.namesContainer}
                  onFocus={e => e.target instanceof HTMLInputElement && setPaymentFieldFocus('name', true)}
                  onBlur={e => e.target instanceof HTMLInputElement && setPaymentFieldFocus('name', false)}
                >
                  <TextInput
                    autoComplete="cc-name"
                    className={classNames(tokenizedStyles.formInput, { [tokenizedStyles.invalidInput]: !!nameError })}
                    errorText={nameError}
                    labelText={<FormattedMessage defaultMessage="Name on card" />}
                    labelTextColor="tertiary"
                    name="name"
                    onChange={(value: string) => {
                      setFullName(value);
                      setClientValidationErrors(CARD_VALIDATION_NAMES.NAME, value);
                    }}
                    placeholder={intl.formatMessage({ defaultMessage: 'Name on card' })}
                    value={fullName}
                  />
                </div>
                {showPostalCode && (
                  <div
                    onFocus={e => e.target instanceof HTMLInputElement && setPaymentFieldFocus('postal', true)}
                    onBlur={e => e.target instanceof HTMLInputElement && setPaymentFieldFocus('postal', false)}
                  >
                    <TextInput
                      autoComplete="postal-code"
                      className={classNames(tokenizedStyles.formInput, { [tokenizedStyles.invalidInput]: !!postalCodeError })}
                      errorText={postalCodeError}
                      labelText={<FormattedMessage defaultMessage="Postal code" />}
                      labelTextColor="tertiary"
                      name="postal code"
                      onChange={(value: string) => {
                        setPostalCode(value);
                        setClientValidationErrors(CARD_VALIDATION_NAMES.POSTAL_CODE, value);
                      }}
                      placeholder={intl.formatMessage({ defaultMessage: 'Postal code' })}
                      value={postalCode}
                    />
                  </div>
                )}
                {showBillingCountry && (
                  <BillingCountrySelector
                    billingCountryCode={billingCountry}
                    labelText={intl.formatMessage({ defaultMessage: 'Billing country' })}
                    onChangeBillingCountry={async e => {
                      const response = await getAvailablePlans(e as CountryCode, purchaseTier);
                      onBillingCountryChange(e as CountryCode, response);
                    }}
                  />
                )}
              </>
            )}
          </div>
          {errors && <ErrorText errors={errors} />}
        </div>
        {isCheckoutModal && (
          <StripeCheckoutModalFooter
            coupon={coupon}
            isRadioUiEnabled={isRadioUiEnabled}
            selectedPaymentType={selectedPaymentType}
            submitting={submitting}
          />
        )}
        {!isCheckoutModal && (
          <div className={classNames(styles.buttonContainer, { [styles.stickyFooter]: isFooterSticky })}>
            {selectedPaymentType !== 'apple_pay' && selectedPaymentType !== process.env.NEXT_PUBLIC_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD && (
              <Button className={styles.button} loading={submitting} text={buttonText} type="submit" variant="primary" />
            )}
            {selectedPaymentType === process.env.NEXT_PUBLIC_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD && <StripePayPalButton submitting={submitting} />}
            {selectedPaymentType === 'apple_pay' && <StripeApplePayButton submitting={submitting} />}
            {isIosNativeApp && (
              <TogglePaymentButton
                selectedPaymentType={
                  selectedPaymentType === 'apple_pay'
                    ? PaymentOptionValues.Apple
                    : selectedPaymentType === 'card'
                      ? PaymentOptionValues.CreditCard
                      : undefined
                }
                setSelectedPaymentType={value => {
                  setSelectedPaymentType(value === PaymentOptionValues.Apple ? 'apple_pay' : 'card');
                  setPaymentMethodOrder(value === PaymentOptionValues.Apple ? ['apple_pay'] : undefined);
                }}
              />
            )}
          </div>
        )}
        {finePrint && !isFinePrintLeftAligned && !isComplianceCheckboxEnabled && <div className={styles.fine}>{finePrint}</div>}
        {promoText && <div className={styles.promoText}>{promoText}</div>}
      </div>
      {expressCheckoutSubmitting && <LoadingOverlay fullscreen={isReiCheckout} />}
    </form>
  );
}

export default StripeCheckoutBase;
